import { useSettledSearch } from '~/composables/unified/useSettledSearch';

/**
 * A search box's text, mirrored into the URL's `keyword` once it settles, so a search survives a
 * refresh and is restored when the student comes back from an opened item. `search` is the raw
 * `v-model`; `keyword` is the debounced, trimmed value to query with. `replace` (not `push`)
 * keeps typing out of browser history, and sibling params (category, filters) are kept. A URL
 * change from elsewhere (a link back to the bare category) is read back into the box.
 */
export const useUrlSearchKeyword = () => {
  const route = useRoute();
  const router = useRouter();
  // String-guarded against `?keyword` arrays.
  const readKeyword = () => (typeof route.query.keyword === 'string' ? route.query.keyword : '');

  const search = ref(readKeyword());
  const { settled, settle } = useSettledSearch(() => search.value);
  const keyword = computed(() => settled.value);

  watch(keyword, (value) => {
    if (value !== readKeyword()) {
      void router.replace({ query: { ...route.query, keyword: value || undefined } });
    }
  });

  // Our own `replace` lands here with the value `keyword` already holds, so only an outside
  // change reaches the box.
  watch(readKeyword, (fromUrl) => {
    if (fromUrl !== keyword.value) {
      search.value = fromUrl;
      settle(fromUrl);
    }
  });

  // Clearing is not typing, so it skips the debounce: the list and the URL follow at once.
  const clear = () => {
    search.value = '';
    settle('');
  };

  return { search, keyword, clear };
};
